import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { useAtomValue } from 'jotai';

import { cn } from '@rocco/utils/cn';
import { Form, FormField } from '@rocco/components/form';
import { useTracker } from '@archipro-website/tracker';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { trackEnquiryFormEngagement } from '~/modules/tracking/util/trackEnquiryFunnelStage';
import { useUserLoggedIn } from '@modules/user';
import { MEMBER_UPDATE_CONTACT_DETAILS_API_ROUTE } from '~/routes/remix-api.member.update-contact-details';
import { useFeature } from '~/modules/root';
import { useGetBuildHubsList } from '@ui-rocco/hooks/use-build-hubs';
import { fileStorage } from '~/utils/file-storage';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import type { PublicEnquiryForm } from '~/modules/enquiry/types/public-enquiry-form';
import type { PredictedDestination } from '~/modules/enquiry/util/predict-enquiry-destination';
import { buildFormContext } from '~/modules/enquiry/domain/configured-enquiry';
import {
    preselectBranchId,
    resolveBranchPicker,
} from '~/modules/enquiry/domain/branch-picker';
import { readDeclinedMessage } from '~/modules/enquiry/domain/enquiry-outcome';
import { useConfiguredEnquiry } from '~/modules/enquiry/hooks/use-configured-enquiry';
import { getUserRegion } from '~/utils/countryHelper';
import { EnquiryGateLocationField } from './EnquiryGateLocationField';
import { EnquiryGateDeclineNotice } from './EnquiryGateResults';
import {
    EnquiryGateContactFields,
    EnquiryGateMessageField,
    EnquiryGateNote,
    EnquiryGateQuestions,
    EnquiryGateSubmitBar,
} from './EnquiryGateFormSections';
import { GateSelectField } from './GateSelectField';
import { GateIdentityFields } from '~/modules-rocco/user/component/auth-modal/GateIdentityFields';
import { FileAttachment } from '~/modules/enquiry/component/FileAttachment';
import { EnquiryProjectDetailsCard } from './EnquiryProjectDetailsCard';
import {
    useEnquiry,
    type EnquiryProfessional,
    type UseEnquiryProps,
} from '~/modules/enquiry/hooks/use-enquiry';
import { useBuildHubFieldSelection } from '~/modules/enquiry/hooks/use-build-hub-field-selection';

export interface EnquiryGateFormProps {
    className?: string;
    /** The professional to submit an enquiry to. */
    professional: EnquiryProfessional & { CustomEnquiryMessage?: string };
    /** The type of the Library Item (professional, product, project, article). */
    itemType: UseEnquiryProps['itemType'];
    /** The ID of the Library Item (professional, product, project, article). */
    siteTreeID: UseEnquiryProps['siteTreeID'];
    /**
     * Bulk selection, submitted in place of the single id above. The backend
     * splits these by owner so each professional receives only their own items.
     */
    siteTreeIDs?: UseEnquiryProps['siteTreeIDs'];
    /** The source of the enquiry. */
    source: UseEnquiryProps['source'];
    /** The project details if the enquiry is for a project. Used for tracking. */
    relatedItem?: UseEnquiryProps['relatedItem'];
    /** Include a branch dropdown if the professional has branches. Defaults to `false`. */
    includeBranch?: boolean;
    /** Disable the submit button (e.g. in preview). Defaults to `false`. */
    disableSubmit?: boolean;
    /** The name of the item being enquired about (e.g. product name). Used to pre-fill the message. */
    itemTitle?: string;
    /** The CTA label that opened the form. Used to tailor the pre-filled message. */
    ctaLabel?: string;
    /** Called when a guest submit comes back auth-required, with the recognised public user. */
    onAuthRequired: (publicUser: PublicUser) => void;
    /**
     * "Not you? Switch account" — sign this member out and hand the surface to
     * the auth step. Omitted where the host has nowhere to land afterwards, in
     * which case the action is not offered.
     */
    onSwitchAccount?: () => void;
    /**
     * The Business Manager form this enquiry fills in. It decides which
     * questions show, what is required, the branch question, the submit label
     * and where the enquiry routes. Null or omitted keeps the stock gate.
     */
    enquiryForm?: PublicEnquiryForm | null;
    /**
     * Called as a validated enquiry is sent, before any sign-in step, with
     * what the host needs to follow it up (e.g. a booking hand-off).
     */
    onSubmitIntent?: (intent: EnquiryGateSubmitIntent) => void;
    /** Called when the server declines the enquiry, with the rule's message. */
    onDeclined?: (message: string) => void;
}

/** What the gate knows about a send at the moment it goes out. */
export interface EnquiryGateSubmitIntent {
    /** Where the client expects it to route. Null without a form. */
    predicted: PredictedDestination | null;
    clientName: string;
    clientEmail: string;
}

const splitName = (name: string | undefined) => {
    const trimmed = (name ?? '').trim();
    if (!trimmed) return { firstName: '', lastName: '' };
    const [firstName, ...rest] = trimmed.split(/\s+/);
    return { firstName: firstName ?? '', lastName: rest.join(' ') };
};

/** A branch select value as the id routing compares, or null when unset. */
const toBranchId = (value: string | undefined): number | null => {
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
};

/** The fields that live behind the identity summary when it is collapsed. */
const IDENTITY_FIELD_NAMES = ['name', 'email', 'phoneNumber'] as const;

/**
 * Enquiry form step of the revamped two-pane gate (BC-4833). Reuses the shared
 * enquiry schema, submit/session logic, file attachments and BuildHub
 * selector; only the layout and field styling are new
 * (the small field tokens from the Figma gating frames, shared with the auth
 * modal). Guest submits that hit the auth wall call `onAuthRequired` so the
 * parent modal can host the auth step in place.
 */
export const EnquiryGateForm = ({
    className,
    professional,
    itemType,
    siteTreeID,
    siteTreeIDs,
    source,
    relatedItem,
    includeBranch = false,
    disableSubmit = false,
    itemTitle,
    ctaLabel,
    onAuthRequired,
    onSwitchAccount,
    enquiryForm = null,
    onSubmitIntent,
    onDeclined,
}: EnquiryGateFormProps) => {
    const { user, isLoggedIn } = useUserLoggedIn();
    // The form's own questions and which of the stock ones it changes. The
    // gate never asks for a location unless a form does.
    const configured = useConfiguredEnquiry(enquiryForm, {
        locationWithoutForm: false,
    });
    const { rules } = configured;
    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);
    const tracker = useTracker();

    const isBuildHubEnabled = useFeature('buildhub');
    const isBuildHubEnquiryProjectDetailsEnabled = useFeature(
        'buildhub_enquiry_project_details',
    );
    const canIncludeProjectDetails =
        isBuildHubEnabled &&
        isBuildHubEnquiryProjectDetailsEnabled &&
        isLoggedIn;

    const [files, setFiles] = useState<File[]>([]);
    const [name, setName] = useState({ firstName: '', lastName: '' });
    const [identityExpanded, setIdentityExpanded] = useState(false);
    /** The reader has worked the disclosure; from here the state is theirs. */
    const identityTouchedRef = useRef(false);
    /** Focus target for a `name` error — see the submit handler. */
    const firstNameRef = useRef<HTMLInputElement | null>(null);
    const formRef =
        useRef<ReturnType<typeof useForm<z.infer<typeof formSchema>>>>(
            undefined,
        );

    const [buildHubs, isBuildHubsLoading, fetchBuildHubs] =
        useGetBuildHubsList();

    const sessionId = useMemo(
        () => `${professional.ID}-${siteTreeID || ''}`,
        [professional.ID, siteTreeID],
    );

    /**
     * The form's branch question when it describes one, otherwise the page's
     * own branch list — which is also the whole story with Forms off.
     */
    const branchPicker = useMemo(
        () =>
            resolveBranchPicker(enquiryForm, {
                includeBranch,
                branchOptions: professional.branchOptions ?? [],
            }),
        [enquiryForm, includeBranch, professional.branchOptions],
    );
    /**
     * Only where the reader has a decision to make. A required select holding
     * a single option is a chore with one legal answer — it still has to reach
     * the submission, so it is chosen for them rather than dropped.
     */
    const showBranchPicker =
        !!branchPicker && branchPicker.branchOptions.length > 1;
    /** The reader chose a branch themselves; location stops choosing for them. */
    const branchTouchedRef = useRef(false);
    /**
     * The branch the location answer last picked, so a later location with no
     * match can clear it again. A branch from a draft or the IP region was not
     * the location's pick and is left alone.
     */
    const locationBranchRef = useRef<string | null>(null);
    // `useEnquiry` validates and saves branch ids against the professional's
    // list, so a form's own branch options stand in for the page's.
    const enquiryProfessional = useMemo(
        () =>
            branchPicker?.source === 'form'
                ? { ...professional, branchOptions: branchPicker.branchOptions }
                : professional,
        [professional, branchPicker],
    );
    // Where the connection geolocates, read once: the draft's own choice and
    // the location answer both outrank it.
    const ipPreferredBranchId = useMemo(
        () =>
            branchPicker
                ? preselectBranchId(branchPicker, { ipRegion: getUserRegion() })
                : null,
        [branchPicker],
    );
    const formContext = useMemo(
        () => buildFormContext(enquiryForm, { declineHandling: 'inline' }),
        [enquiryForm],
    );
    /** Read by `onFailure`, which `useEnquiry` calls from its own effect. */
    const fetcherDataRef = useRef<unknown>(undefined);

    const {
        defaultFormValues,
        formSchema,
        state,
        fetcherData,
        submitEnquiry,
        saveEnquirySession,
        patchEnquirySession,
        saveSuburbToProfile,
        messagePlaceholder,
    } = useEnquiry({
        professional: enquiryProfessional,
        itemType,
        siteTreeID,
        siteTreeIDs,
        source,
        relatedItem,
        includeBranch: !!branchPicker,
        branchRequired: branchPicker?.required ?? true,
        preferredBranchId: ipPreferredBranchId ?? undefined,
        collectLocation: rules.collectLocation,
        messageEnabled: rules.messageEnabled,
        messageRequired: rules.messageRequired,
        formContext,
        isModal: true,
        itemTitle,
        ctaLabel,
        onSuccess: () => {
            formRef.current?.reset();
            setFiles([]);
            configured.reset();
            fileStorage.clearFiles(sessionId).catch(console.error);
        },
        onFailure: () => {
            const declined = readDeclinedMessage(fetcherDataRef.current);
            if (declined !== null) onDeclined?.(declined);
        },
        onAuthRequired,
    });
    fetcherDataRef.current = fetcherData;

    /** A submit is on the wire — the fetcher is loading or posting. */
    const isSending = state !== 'idle';

    useEffect(() => {
        if (!canIncludeProjectDetails) return;
        if (!buildHubs && !isBuildHubsLoading) {
            fetchBuildHubs();
        }
    }, [
        buildHubs,
        fetchBuildHubs,
        canIncludeProjectDetails,
        isBuildHubsLoading,
    ]);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        disabled: state !== 'idle',
        mode: 'onChange',
        defaultValues: defaultFormValues,
    });
    formRef.current = form;

    // Funnel stage: the user started filling the form (once per rendered form).
    // Watch genuine input events ('change'), not formState.isDirty — restores
    // and programmatic writes must not count as engagement. The name inputs
    // write via setValue (watch reports no type), so syncName marks it too.
    const isFormEngagementTracked = useRef(false);
    const markFormEngagement = useCallback(() => {
        if (isFormEngagementTracked.current) return;
        isFormEngagementTracked.current = true;
        trackEnquiryFormEngagement(tracker, {
            professionalName: professional.Title,
            itemID: siteTreeID,
            itemType,
            source,
            professionalID: professional.ID,
        });
    }, [tracker, professional, itemType, siteTreeID, source]);
    useEffect(() => {
        const subscription = form.watch((_values, { type }) => {
            if (type !== 'change') return;
            markFormEngagement();
        });
        return () => subscription.unsubscribe();
    }, [form, markFormEngagement]);

    const selectedBuildHubId = form.watch('buildHubId');
    const [fieldSelection, setFieldSelection] =
        useBuildHubFieldSelection(selectedBuildHubId);

    useEffect(() => {
        if (canIncludeProjectDetails || !selectedBuildHubId) return;
        form.setValue('buildHubId', undefined, { shouldDirty: true });
        // Only the field this effect owns, merged into whatever the session
        // now holds. `canIncludeProjectDetails` is derived from `isLoggedIn`,
        // so signing out fires this — and saving the whole form back put the
        // outgoing member's name, email, phone and suburb straight into the
        // session that "Not you? Switch account" had just cleared them from,
        // ready for whoever signed in next.
        patchEnquirySession({ buildHubId: undefined });
    }, [
        canIncludeProjectDetails,
        form,
        patchEnquirySession,
        selectedBuildHubId,
    ]);

    const handleFileChange = useCallback(
        async (newFiles: File[]) => {
            setFiles(newFiles);
            await fileStorage.saveFiles(sessionId, newFiles);
            saveEnquirySession(form.getValues(), newFiles);
        },
        [sessionId, form, saveEnquirySession],
    );

    useEffect(() => {
        if (!form.formState.isDirty && !form.formState.isSubmitted) {
            // Load the saved session data / prefilled defaults.
            form.reset(defaultFormValues);
            setName(splitName(defaultFormValues.name));

            fileStorage.getFiles(sessionId).then((restoredFiles) => {
                if (restoredFiles.length) {
                    setFiles(restoredFiles);
                }
            });
        }
    }, [defaultFormValues, form, sessionId]);

    const syncName = useCallback(
        (firstName: string, lastName: string) => {
            // Only ever called from the name inputs' onChange, so this is
            // genuine typing — setValue doesn't surface through form.watch
            // with a 'change' type, mark engagement here.
            markFormEngagement();
            setName({ firstName, lastName });
            form.setValue('name', `${firstName} ${lastName}`.trim(), {
                shouldDirty: true,
                shouldValidate: form.formState.isSubmitted,
            });
        },
        [form, markFormEngagement],
    );

    const watchedEmail = form.watch('email');
    const watchedPhone = form.watch('phoneNumber');

    // Live routing prediction. Answers that match a rule showing a message
    // replace the message box and the send button straight away, so nobody
    // writes an enquiry that was never going to be sent.
    const watchedRegion = form.watch('postcode')?.value?.region ?? null;
    const watchedBranchId = toBranchId(form.watch('branchId'));

    // A location already filled in (from the account or an earlier visit) picks the branch
    // the same way a typed one does, until the reader chooses for themselves.
    const watchedPlace = form.watch('postcode')?.value ?? null;
    useEffect(() => {
        if (!branchPicker || branchTouchedRef.current || !watchedPlace) return;
        if (form.getValues('branchId')) return;
        const branchId = preselectBranchId(branchPicker, {
            place: watchedPlace,
            regionName: watchedPlace.region,
        });
        if (branchId === null) return;
        locationBranchRef.current = String(branchId);
        form.setValue('branchId', String(branchId));
    }, [branchPicker, form, watchedPlace]);

    const { predict } = configured;
    const declineMessage = useMemo(
        () => predict(watchedRegion, watchedBranchId)?.message ?? null,
        [predict, watchedRegion, watchedBranchId],
    );

    const selectLocation = useCallback(
        (place: z.infer<typeof formSchema>['postcode'] | undefined) => {
            form.setValue('postcode', place, {
                shouldDirty: true,
                shouldValidate: form.formState.isSubmitted,
            });
            saveEnquirySession({ ...form.getValues(), postcode: place });
            if (place) saveSuburbToProfile(place);
            // The location answer picks the branch until the reader does. The
            // page's own list only ever fills an empty choice, or replaces the
            // location's own earlier pick.
            if (!branchPicker || branchTouchedRef.current) return;
            const current = form.getValues('branchId') ?? '';
            const currentIsLocationPick =
                current !== '' && current === locationBranchRef.current;
            if (
                branchPicker.source === 'network' &&
                current &&
                !currentIsLocationPick
            ) {
                return;
            }
            const branchId = place
                ? preselectBranchId(branchPicker, {
                      place: place.value,
                      regionName: place.value.region,
                  })
                : null;
            if (branchId !== null) {
                locationBranchRef.current = String(branchId);
                form.setValue('branchId', String(branchId), {
                    shouldDirty: true,
                });
            } else if (currentIsLocationPick) {
                // A location with no match no longer vouches for the branch the
                // last one picked.
                locationBranchRef.current = null;
                form.setValue('branchId', '', { shouldDirty: true });
            }
        },
        [form, branchPicker, saveEnquirySession, saveSuburbToProfile],
    );

    /**
     * The account behind the prefilled fields. Guests get `null` — there is
     * nothing to summarise, so they see the fields themselves.
     */
    const identity = useMemo(() => {
        if (!isLoggedIn) return null;
        // The name inputs are seeded by the re-hydration effect, so on the
        // first paint the local state is still empty. Fall back to the
        // defaults it is about to be seeded from, or the summary shows the
        // reader's email for a frame where their name belongs.
        const seeded = name.firstName || name.lastName;
        const fallback = splitName(defaultFormValues.name);
        return {
            firstName: seeded ? name.firstName : fallback.firstName,
            lastName: seeded ? name.lastName : fallback.lastName,
            email: watchedEmail ?? '',
            phone: watchedPhone || undefined,
            avatarUrl: user.ProfileImageLink || user.ProfileImage || undefined,
        };
    }, [isLoggedIn, name, defaultFormValues, watchedEmail, watchedPhone, user]);

    /**
     * A member who cannot send as-is starts on the fields, not on a summary
     * that hides what is missing — the phone is required to submit and is the
     * one most often absent from a profile.
     *
     * Tracks the defaults rather than latching on the first pass. The saved
     * draft lives in `sessionStorage` and is not read until after the first
     * client render, so a member whose phone is in the draft but not on their
     * profile reads as incomplete for one pass; latching there would strand
     * them expanded. Following the defaults lets that correct itself when the
     * draft lands.
     *
     * Stops the moment the reader works the disclosure themselves — from then
     * on the state is theirs, and finishing the last missing field must never
     * collapse the card mid-edit.
     */
    useEffect(() => {
        if (!isLoggedIn || identityTouchedRef.current) return;
        const filled = (value: unknown) =>
            typeof value === 'string' ? !!value.trim() : !!value;
        setIdentityExpanded(
            !filled(defaultFormValues.name) ||
                !filled(defaultFormValues.email) ||
                !filled(defaultFormValues.phoneNumber),
        );
    }, [isLoggedIn, defaultFormValues]);

    /**
     * The form's contact details against what the account holds. Comparing to
     * the profile rather than to a snapshot taken when the panel opened means
     * a reader who edits a field and puts it back is not offered a save that
     * writes nothing, and a panel that opened itself does not claim changes
     * nobody made.
     */
    const identitySnapshot = useMemo(
        () => ({
            FirstName: name.firstName.trim(),
            Surname: name.lastName.trim(),
            Phone: (watchedPhone ?? '').trim(),
        }),
        [name, watchedPhone],
    );
    const identityOnAccount = useMemo(() => {
        const onAccount = splitName(defaultFormValues.name);
        return {
            FirstName: onAccount.firstName.trim(),
            Surname: onAccount.lastName.trim(),
            Phone: (defaultFormValues.phoneNumber ?? '').trim(),
        };
    }, [defaultFormValues]);
    const identityChanges = Object.fromEntries(
        Object.entries(identitySnapshot).filter(
            ([field, value]) =>
                // Never an empty one. The write is a partial update, so a
                // blank field posted as `''` is stored as `''` — clearing the
                // phone here to retype it and closing the panel mid-way would
                // wipe the number off the account. Emptying a detail is not
                // something this form can express: all three are required to
                // send, so an empty value is only ever a half-finished edit.
                value !== '' &&
                identityOnAccount[field as keyof typeof identityOnAccount] !==
                    value,
        ),
    );
    const identityDirty = Object.keys(identityChanges).length > 0;

    const contactFetcher = useFetcher();
    const handleIdentityExpandedChange = useCallback(
        (next: boolean) => {
            identityTouchedRef.current = true;
            setIdentityExpanded(next);
            if (next) return;
            // Closing is optimistic: the summary shows the new values straight
            // away and the write goes out behind it. Nothing downstream waits
            // on the profile — the enquiry carries its own copy of these
            // fields — so a write that fails costs the reader nothing here
            // beyond having to correct the same detail again next time.
            if (Object.keys(identityChanges).length) {
                contactFetcher.submit(identityChanges, {
                    method: 'POST',
                    action: MEMBER_UPDATE_CONTACT_DETAILS_API_ROUTE,
                });
            }
        },
        [contactFetcher, identityChanges],
    );

    const errors = form.formState.errors;
    const emailDisabled = isLoggedIn && !!user.Email;
    const emailReg = form.register('email');
    const phoneReg = form.register('phoneNumber');
    const messageReg = form.register('message');

    return (
        <div
            className={cn(
                // `flex-1` so a short form still fills the pane and the CTA
                // can sit against the bottom edge rather than floating under
                // the last field. A signed-in reader's form is short: five
                // fields collapse to one row.
                'mx-auto flex w-full max-w-93.75 flex-1 flex-col',
                className,
            )}
        >
            {/* FormProvider context for Rocco form primitives rendered inside
                (BuildHubSelector's FormItem/FormLabel read it via useFormField). */}
            <Form {...form}>
                <form
                    suppressHydrationWarning
                    id="enquiry-gate-form"
                    onSubmit={form.handleSubmit(
                        (data) => {
                            if (state !== 'idle') return;
                            if (!configured.validate()) return;
                            const { declined, predicted, selection } =
                                configured.prepareSubmission(
                                    data.postcode?.value?.region ?? null,
                                    toBranchId(data.branchId),
                                );
                            // The decline notice is already on screen.
                            if (declined) return;
                            onSubmitIntent?.({
                                predicted,
                                clientName: data.name,
                                clientEmail:
                                    isLoggedIn && user.Email
                                        ? user.Email
                                        : data.email,
                            });
                            // Files restored from an earlier draft must not
                            // ride along on a form without the attach control.
                            submitEnquiry(
                                data,
                                rules.attachmentsEnabled ? files : [],
                                fieldSelection,
                                selection,
                            );
                        },
                        (submitErrors) => {
                            // Every problem at once, the form's own questions
                            // included, so fixing one does not just reveal
                            // the next.
                            configured.validate();
                            // An invalid contact field is unreachable behind a
                            // collapsed summary. Open it and put the cursor in
                            // the offending field: the reader pressed Continue
                            // from the pinned footer, so a panel that expands
                            // above the fold without focus moving reads as the
                            // button having done nothing.
                            const invalidIdentityField =
                                IDENTITY_FIELD_NAMES.find(
                                    (field) => field in submitErrors,
                                );
                            if (invalidIdentityField) {
                                handleIdentityExpandedChange(true);
                                // After paint: the field is still hidden this
                                // tick, and focus does not land on a
                                // `display: none` input.
                                requestAnimationFrame(() => {
                                    // `name` is not registered — it is written
                                    // through `setValue` from two local inputs
                                    // — so RHF holds no ref to focus and the
                                    // card would open with the cursor nowhere.
                                    if (invalidIdentityField === 'name') {
                                        firstNameRef.current?.focus();
                                        return;
                                    }
                                    form.setFocus(invalidIdentityField);
                                });
                            }
                            // Field names only — never values.
                            const fields = Object.keys(submitErrors).join(',');
                            tracker.log('EnquiryValidationError', {
                                url: new URL(window.location.href),
                                data: {
                                    Source: source,
                                    fields,
                                    ExtraData: JSON.stringify({ fields }),
                                },
                            });
                        },
                    )}
                    className="flex flex-1 flex-col gap-5.5"
                >
                    {/* The professional's own words. They ride in the brand
                        pane on desktop, and the sheet does not render that
                        pane — without this they are simply absent on a phone,
                        which is the one surface where a supplier's "email us
                        instead" note is most likely to be acted on. */}
                    {!isDesktop && !!professional.CustomEnquiryMessage && (
                        <EnquiryGateNote>
                            {professional.CustomEnquiryMessage}
                        </EnquiryGateNote>
                    )}
                    {/* Contact details. A signed-in reader sees these as one
                        summary row until they choose to edit; a guest sees the
                        fields themselves. */}
                    <GateIdentityFields
                        identity={identity}
                        expanded={identityExpanded}
                        onExpandedChange={handleIdentityExpandedChange}
                        disabled={form.formState.disabled}
                        onSwitchAccount={onSwitchAccount}
                        dirty={identityDirty}
                    >
                        <EnquiryGateContactFields
                            firstName={name.firstName}
                            lastName={name.lastName}
                            onNameChange={syncName}
                            onNameBlur={() =>
                                saveEnquirySession(form.getValues())
                            }
                            firstNameRef={firstNameRef}
                            emailInput={{
                                ...emailReg,
                                onBlur: (e) => {
                                    emailReg.onBlur(e);
                                    saveEnquirySession(form.getValues());
                                },
                            }}
                            phoneInput={{
                                ...phoneReg,
                                onBlur: (e) => {
                                    phoneReg.onBlur(e);
                                    saveEnquirySession(form.getValues());
                                },
                            }}
                            emailLocked={emailDisabled}
                            errors={{
                                name: errors.name?.message,
                                email: errors.email?.message,
                                phoneNumber: errors.phoneNumber?.message,
                            }}
                            disabled={form.formState.disabled}
                            twoColumns={isDesktop}
                        />
                    </GateIdentityFields>

                    <EnquiryGateQuestions
                        rules={rules}
                        answers={configured.answers}
                        errors={configured.errors}
                        visitorCountry={configured.visitorCountry}
                        twoColumns={isDesktop}
                        disabled={form.formState.disabled}
                        onEnquiryTypeChange={configured.setEnquiryTypeId}
                        onEnquirerTypeChange={configured.setEnquirerType}
                        onProjectKindChange={configured.setProjectKind}
                        onFieldAnswerChange={configured.setFieldAnswer}
                        location={
                            rules.collectLocation && (
                                <Controller
                                    name="postcode"
                                    control={form.control}
                                    render={({ field }) => (
                                        <EnquiryGateLocationField
                                            label={rules.locationLabel}
                                            value={field.value ?? undefined}
                                            error={errors.postcode?.message}
                                            disabled={form.formState.disabled}
                                            onSelect={selectLocation}
                                            onBlur={field.onBlur}
                                        />
                                    )}
                                />
                            )
                        }
                        // Only where there is a choice to make.
                        branch={
                            showBranchPicker && (
                                <Controller
                                    name="branchId"
                                    control={form.control}
                                    render={({ field }) => (
                                        <GateSelectField
                                            label={branchPicker.label}
                                            placeholder="Select a branch"
                                            value={field.value ?? ''}
                                            options={branchPicker.branchOptions.map(
                                                (branch) => ({
                                                    value: branch.value.branchId.toString(),
                                                    label: branch.header,
                                                }),
                                            )}
                                            error={errors.branchId?.message}
                                            disabled={form.formState.disabled}
                                            onBlur={field.onBlur}
                                            onChange={(value) => {
                                                // The select reports an empty value on its own while
                                                // its options mount; a reader can only pick a branch.
                                                if (!value) return;
                                                branchTouchedRef.current = true;
                                                field.onChange(value);
                                                saveEnquirySession({
                                                    ...form.getValues(),
                                                    branchId: value,
                                                });
                                            }}
                                        />
                                    )}
                                />
                            )
                        }
                    />

                    {declineMessage !== null && (
                        <EnquiryGateDeclineNotice message={declineMessage} />
                    )}

                    {declineMessage === null && rules.messageEnabled && (
                        <EnquiryGateMessageField
                            {...messageReg}
                            required={rules.messageRequired}
                            placeholder={messagePlaceholder}
                            error={errors.message?.message}
                            disabled={form.formState.disabled}
                            onBlur={(e) => {
                                messageReg.onBlur(e);
                                saveEnquirySession(form.getValues());
                            }}
                        />
                    )}

                    {/* Every surface. The legacy form hid this below desktop and
                        the revamp carried that across, which had it backwards:
                        a phone is where attaching is easiest — camera roll, or
                        a photo taken on the spot — and an enquiry carrying a
                        plan or a photo of the space is a far better lead than
                        the same message alone. */}
                    {declineMessage === null && rules.attachmentsEnabled && (
                        <FileAttachment
                            variant="compact"
                            files={files}
                            onChange={handleFileChange}
                            maxAttachments={5}
                            disabled={form.formState.disabled}
                        />
                    )}

                    {/* BuildHub project details (logged-in, flags on) — all viewports, matching legacy. */}
                    {canIncludeProjectDetails && (
                        <FormField
                            name="buildHubId"
                            control={form.control}
                            render={({ field }) => (
                                <EnquiryProjectDetailsCard
                                    value={field.value}
                                    onChange={(value) => {
                                        // `setValue`, not `field.onChange`:
                                        // the controller's handler reads its
                                        // argument as an event payload and
                                        // ignores `undefined`, so detaching a
                                        // project cleared the saved draft but
                                        // left the field — and the enquiry —
                                        // still holding it.
                                        form.setValue('buildHubId', value, {
                                            shouldDirty: true,
                                        });
                                        saveEnquirySession({
                                            ...form.getValues(),
                                            buildHubId: value,
                                        });
                                    }}
                                    disabled={field.disabled}
                                    onBlur={field.onBlur}
                                    buildHubs={buildHubs}
                                    isLoading={isBuildHubsLoading}
                                    fieldSelection={fieldSelection}
                                    onFieldSelectionChange={setFieldSelection}
                                    onRefetchBuildHubs={fetchBuildHubs}
                                    professionalTitle={professional.Title}
                                />
                            )}
                        />
                    )}

                    <EnquiryGateSubmitBar
                        consentText={rules.consentText}
                        showSubmit={declineMessage === null}
                        label={rules.submitLabel}
                        isSending={isSending}
                        disabled={disableSubmit || form.formState.disabled}
                    />
                </form>
            </Form>
        </div>
    );
};
